import { Box, Card, CardContent, Skeleton, Typography } from '@mui/material';
import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
	selectOrderReturns,
	selectOrderReturnsData,
	selectOrderReturnsLoading,
	selectOrderReturnItemsData,
	selectOrderReturnItemsLoading,
} from 'redux/reducers/orderManagement/selectors';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { TOrderReturnItem } from 'redux/reducers/orderManagement/types';
import { TOrderReturnsCardProps } from './types';
import { OrderReturnsItem } from './OrderReturnsItem';
import { EasCard } from '../../../../ui/EasCard';

export const OrderReturns: FC<TOrderReturnsCardProps> = ({ paymentCurrency }) => {
	const orderReturns = useSelector(selectOrderReturns);
	const orderReturnsRows = useSelector(selectOrderReturnsData) as TOrderReturnItem[];
	const orderReturnsLoading = useSelector(selectOrderReturnsLoading);

	const orderReturnItemsRows = useSelector(selectOrderReturnItemsData);
	const orderReturnItemsLoading = useSelector(selectOrderReturnItemsLoading);

	const totalReturnAmount = useMemo(() => {
		return orderReturnsRows.reduce(
			(accumulator: number, item: TOrderReturnItem) =>
				accumulator + (item.total_return_amount ?? 0),
			0,
		);
	}, [orderReturnsRows]);

	if (orderReturnsLoading || orderReturnItemsLoading) {
		return <Skeleton variant="rectangular" height="300px" sx={{ borderRadius: '12px', mb: 2 }} />;
	}

	if (!orderReturns || !orderReturnsRows.length) {
		return null;
	}

	return (
		<EasCard
			id="order-returns-card"
			title={`Order Returns (${orderReturnsRows.length})`}
			action={<></>}
			loading={orderReturnsLoading || orderReturnItemsLoading}
		>
			<Typography variant="h6" component="div">
				{orderReturnsRows.map((item) => (
					<OrderReturnsItem
						key={item.id}
						orderReturnsItem={item}
						returnedOrderBreakdown={orderReturnItemsRows.filter(
							(i) => i.return_id === item.id && i.timestamp_year === item.timestamp_year,
						)}
						paymentCurrency={paymentCurrency}
					/>
				))}
			</Typography>

			{orderReturnsRows.length > 1 && (
				<Box
					sx={{
						mt: 1.5,
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						border: '1px solid #00000020',
						borderRadius: '12px',
						padding: '10px',
					}}
				>
					<Typography variant="h6" sx={{ fontWeight: 'bold' }} color="primary">
						Total Return Amount:
					</Typography>
					<AmountAndCurrency
						amount={totalReturnAmount}
						currency={paymentCurrency}
						bold="bold"
						size={1.3}
						color="primary"
					/>
				</Box>
			)}
		</EasCard>
	);
};
